import { Button, Drawer, Group, Title, useMantineTheme } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import ClientEditForm from 'components/shared/ClientEditForm';
import SubAgentEditForm from 'components/shared/SubAgentEditForm';
import { useCreateMeetingDrawerModal } from 'stores/global/CreateMeetingDrawerModal';
import { ClientEditFieldsKey } from 'stores/global/RootStore/AppConfigStore';
import { CreateMeetingDrawerModalMode } from 'types/CreateMeetingDrawerModalMode';
import { useDevices } from 'utils/devices';

import Text from '../Text';

import CreateForm from './CreateForm';
import CreateWithClient from './CreateWithClient';
import CreateWithLead from './CreateWithLead';
import CreateWithNewLead from './CreateWithNewLead';

import s from './CreateMeetingDrawer.module.scss';

const CreateMeetingDrawer: React.FC = () => {
  const theme = useMantineTheme();
  const { isPhone } = useDevices();
  const createMeetingDrawerModal = useCreateMeetingDrawerModal();
  const {
    close,
    isOpen,
    createMeeting,
    createLoadingStage,
    isError,
    isTouched,
    isStaticMode,
    createClientStore,
    createSubAgentStore,
    clientUniquenessStore,
  } = createMeetingDrawerModal;

  const CreateClientComponent = React.useCallback(
    () => (
      <CreateForm title="Новый клиент" onSubmit={createClientStore.nextStep}>
        <ClientEditForm store={createClientStore} />
      </CreateForm>
    ),
    [createClientStore],
  );

  const CreateSubAgentComponent = React.useCallback(
    () => (
      <CreateForm title="Новый субагент" onSubmit={createSubAgentStore.nextStep}>
        <SubAgentEditForm store={createSubAgentStore} />
      </CreateForm>
    ),
    [createSubAgentStore],
  );

  const createMap: Record<CreateMeetingDrawerModalMode, React.FC> = React.useMemo(
    () => ({
      [CreateMeetingDrawerModalMode.openNewMeeting]: CreateWithNewLead,
      [CreateMeetingDrawerModalMode.openWithClientAndLead]: CreateWithLead,
      [CreateMeetingDrawerModalMode.openWithClient]: CreateWithClient,
      [CreateMeetingDrawerModalMode.openCreateClient]: CreateClientComponent,
      [CreateMeetingDrawerModalMode.openCreateSubAgent]: CreateSubAgentComponent,
    }),
    [CreateClientComponent, CreateSubAgentComponent],
  );

  const CreateComponent = isStaticMode.value && createMap[isStaticMode.value];

  const renderFooterButton = () => {
    if (isStaticMode.value === CreateMeetingDrawerModalMode.openCreateClient) {
      return (
        <Button
          fullWidth
          onClick={createClientStore.submit}
          loading={createClientStore.loadingStage.isLoading}
          disabled={!createClientStore.fieldsMap[ClientEditFieldsKey.phone].shouldShowFields}
        >
          <Text size="lg">Создать клиента</Text>
        </Button>
      );
    }

    if (isStaticMode.value === CreateMeetingDrawerModalMode.openCreateSubAgent) {
      return (
        <Button fullWidth onClick={createSubAgentStore.submit} loading={createSubAgentStore.loadingStage.isLoading}>
          <Text size="lg">Создать субагента</Text>
        </Button>
      );
    }

    return (
      <Button
        fullWidth
        onClick={createMeeting}
        disabled={isError || !isTouched || clientUniquenessStore.isClientNotUnique}
        loading={createLoadingStage.isLoading}
      >
        <Text size="lg" c={isError || !isTouched || clientUniquenessStore.isClientNotUnique ? 'gray' : 'white'}>
          Назначить встречу
        </Text>
      </Button>
    );
  };

  return (
    <Drawer.Root
      opened={isOpen}
      onClose={close}
      position="right"
      size={isPhone ? '100%' : 'md'}
      offset={isPhone ? 0 : theme.spacing.lg}
      radius={isPhone ? 0 : 'md'}
    >
      <Drawer.Overlay />
      <Drawer.Content>
        <Drawer.Body className={s.root} p={0}>
          <Group wrap="nowrap" align="flex-start" justify="space-between" className={s.root__header}>
            <Title size={isPhone ? 'h3' : 'h2'} fw={600}>
              Новая встреча
            </Title>
            <Drawer.CloseButton onClick={close} size={32} icon={<IconX size={24} />} />
          </Group>
          <div className={s.root__content}>{CreateComponent && <CreateComponent />}</div>
          <div className={s.root__footer}>{renderFooterButton()}</div>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
};

export default observer(CreateMeetingDrawer);
